/**
 * Table Section JavaScript
 * Handles editor-time cell synchronization and mobile behavior
 */

class TableSection {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.isEditor = window.Shopify && window.Shopify.designMode;

    this.init();
  }

  init() {
    if (this.isEditor) {
      this.observeSettingsChanges();
    }

    this.initMobileBehavior();
    this.addKeyboardNavigation();
  }

  /**
   * Automatically create/update cells to match rows × columns
   */
  async syncCells() {
    const rows = parseInt(this.container.dataset.rows) || 3;
    const columns = parseInt(this.container.dataset.columns) || 3;
    const totalNeeded = rows * columns;

    try {
      // Get current blocks
      const currentBlocks = await this.getCurrentBlocks();
      const currentCells = currentBlocks.filter(b => b.type === '_1-table-cell');

      // Create a map of existing cells by position
      const cellMap = new Map();
      currentCells.forEach(cell => {
        const key = `${cell.settings.row}-${cell.settings.column}`;
        cellMap.set(key, cell);
      });

      // Determine which cells need to be created
      const cellsToCreate = [];
      for (let row = 1; row <= rows; row++) {
        for (let col = 1; col <= columns; col++) {
          const key = `${row}-${col}`;
          if (!cellMap.has(key)) {
            cellsToCreate.push({ row, col });
          }
        }
      }

      // Create missing cells
      if (cellsToCreate.length > 0) {
        await this.createCells(cellsToCreate);
        this.showNotification(`Created ${cellsToCreate.length} cell(s)`);
      } else {
        this.showNotification('All cells are already in place');
      }

      // Remove cells that are out of bounds
      const cellsToRemove = currentCells.filter(cell =>
        cell.settings.row > rows || cell.settings.column > columns
      );

      if (cellsToRemove.length > 0) {
        await this.removeCells(cellsToRemove);
        this.showNotification(`Removed ${cellsToRemove.length} out-of-bounds cell(s)`);
      }

    } catch (error) {
      console.error('Error syncing cells:', error);
      this.showNotification('Error syncing cells. See console for details.', 'error');
    }
  }

  /**
   * Get current blocks from the section
   */
  async getCurrentBlocks() {
    // In the Shopify theme editor, we can access section data via the DOM
    const blocks = [];
    const blockElements = this.container.querySelectorAll('[data-block-id]');

    blockElements.forEach(el => {
      const blockId = el.dataset.blockId;
      const row = parseInt(el.closest('tr')?.dataset.row) || 1;
      const col = Array.from(el.parentElement.children).indexOf(el) + 1;

      blocks.push({
        id: blockId,
        type: '_1-table-cell',
        settings: { row, column: col }
      });
    });

    return blocks;
  }

  /**
   * Create cells using Section Rendering API
   * Note: This is a simplified version. In practice, you'd need to use
   * Shopify's Admin API or Section Rendering API to create blocks
   */
  async createCells(cells) {
    // In the theme editor, we need to guide the user to add blocks manually
    // or use a more advanced approach with the Admin API

    const cellList = cells.map(c => `Row ${c.row}, Col ${c.col}`).join('; ');

    console.log('Cells to create:', cellList);

    // Show instructions to user
    const message = `Please add ${cells.length} "1 Table Cell" block(s) with these positions:\n${cellList}`;
    alert(message);

    return Promise.resolve();
  }

  /**
   * Remove cells that are out of bounds
   */
  async removeCells(cells) {
    const message = `${cells.length} cell(s) are out of the current table bounds. Please remove them manually from the block list.`;
    alert(message);
    return Promise.resolve();
  }

  /**
   * Show notification to user
   */
  showNotification(message, type = 'info') {
    const info = this.container.querySelector('.table-editor-controls__info');
    if (info) {
      info.textContent = message;
      info.style.color = type === 'error' ? '#d00' : 'inherit';

      setTimeout(() => {
        const rows = parseInt(this.container.dataset.rows) || 3;
        const columns = parseInt(this.container.dataset.columns) || 3;
        const current = this.container.querySelectorAll('[data-block-id]').length;
        const needed = rows * columns;
        info.textContent = `Current: ${current} cells | Needed: ${needed} cells`;
        info.style.color = '';
      }, 5000);
    }
  }

  /**
   * Observe changes to rows/columns settings in the editor
   */
  observeSettingsChanges() {
    // Listen for section:load event from Shopify theme editor
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === this.sectionId) {
        this.updateCellInfo();
      }
    });

    // Update info on initial load
    this.updateCellInfo();
  }

  /**
   * Update the cell info display
   */
  updateCellInfo() {
    const rows = parseInt(this.container.dataset.rows) || 3;
    const columns = parseInt(this.container.dataset.columns) || 3;
    const current = this.container.querySelectorAll('[data-block-id]').length;
    const needed = rows * columns;

    const info = this.container.querySelector('.table-editor-controls__info');
    if (info) {
      info.textContent = `Current: ${current} cells | Needed: ${needed} cells`;
    }
  }

  /**
   * Initialize mobile behavior
   */
  initMobileBehavior() {
    const wrapper = this.container.querySelector('.table-wrapper');
    const table = this.container.querySelector('.custom-table');

    if (!wrapper || !table) return;

    // For stacked mobile view, add column headers as data attributes
    if (wrapper.classList.contains('table-wrapper--stack')) {
      this.addMobileColumnHeaders();
    }

    // Add scroll indicators for horizontal scroll
    if (wrapper.classList.contains('table-wrapper--scroll')) {
      this.addScrollIndicators();
    }
  }

  /**
   * Add column headers to cells for mobile stacked view
   */
  addMobileColumnHeaders() {
    const headerCells = this.container.querySelectorAll('.custom-table__row--header .custom-table__cell');
    const headers = Array.from(headerCells).map(cell => cell.textContent.trim());

    const rows = this.container.querySelectorAll('.custom-table__row:not(.custom-table__row--header)');
    rows.forEach(row => {
      const cells = row.querySelectorAll('.custom-table__cell');
      cells.forEach((cell, index) => {
        if (headers[index]) {
          cell.setAttribute('data-column-header', headers[index]);
        }
      });
    });
  }

  /**
   * Add scroll indicators for horizontal scrolling tables
   */
  addScrollIndicators() {
    const wrapper = this.container.querySelector('.table-wrapper--scroll');
    if (!wrapper) return;

    wrapper.addEventListener('scroll', () => {
      const isScrolledToEnd = wrapper.scrollLeft >= (wrapper.scrollWidth - wrapper.clientWidth - 5);
      const isScrolledToStart = wrapper.scrollLeft <= 5;

      wrapper.classList.toggle('is-scrolled-start', isScrolledToStart);
      wrapper.classList.toggle('is-scrolled-end', isScrolledToEnd);
    });

    // Trigger initial check
    wrapper.dispatchEvent(new Event('scroll'));
  }

  /**
   * Add keyboard navigation to table
   */
  addKeyboardNavigation() {
    const cells = this.container.querySelectorAll('.custom-table__cell');

    cells.forEach((cell, index) => {
      // Make cells focusable
      if (!cell.hasAttribute('tabindex') && !this.isEditor) {
        cell.setAttribute('tabindex', '0');
      }

      cell.addEventListener('keydown', (e) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          return;
        }

        e.preventDefault();

        const row = cell.closest('tr');
        const cellIndex = Array.from(row.children).indexOf(cell);
        let targetCell = null;

        switch (e.key) {
          case 'ArrowRight':
            targetCell = cell.nextElementSibling;
            break;
          case 'ArrowLeft':
            targetCell = cell.previousElementSibling;
            break;
          case 'ArrowDown':
            const nextRow = row.nextElementSibling;
            if (nextRow) {
              targetCell = nextRow.children[cellIndex];
            }
            break;
          case 'ArrowUp':
            const prevRow = row.previousElementSibling;
            if (prevRow) {
              targetCell = prevRow.children[cellIndex];
            }
            break;
        }

        if (targetCell) {
          targetCell.focus();
        }
      });
    });
  }
}

// Initialize all table sections
document.addEventListener('DOMContentLoaded', () => {
  const tableSections = document.querySelectorAll('.table-section');
  tableSections.forEach(section => new TableSection(section));
});

// Re-initialize when section is reloaded in editor
document.addEventListener('shopify:section:load', (event) => {
  const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
  if (section && section.classList.contains('table-section')) {
    new TableSection(section);
  }
});
